// variables
import { MAIN_OBJECT } from 'variables';

// libraries
import _ from 'lodash-es';
import gsap from 'gsap';
import { folder } from 'leva';
import { Box3, MathUtils, Vector3 } from 'three';
import dayjs from 'dayjs';

export const debouncedFunction = _.debounce((fn) => fn(), 300);

export const modelChangeObjectToLeva = (object, controls) => {
  const objectName = object.name || MAIN_OBJECT;

  if (object.children && object.children.length > 0) {
    const childControls = {};

    object.children.forEach((child) => {
      modelChangeObjectToLeva(child, childControls, objectName);
    });

    controls[objectName] = folder(
      {
        [`visible_${objectName}`]: object.visible,
        ...childControls
      },
      { collapsed: true }
    );
  } else {
    controls[objectName] = {
      [`visible_${objectName}`]: object.visible
    };
  }
};

/**
 * 모델링이 화면에 보이도록 카메라 위치 조정
 */
export const zoomFit = (object3D, cameraControlsRef, viewMode, bFront, cameraConfigRef) => {
  const box = new Box3().setFromObject(object3D);
  const sizeBox = box.getSize(new Vector3()).length();
  const centerBox = box.getCenter(new Vector3());

  let offsetX = 0,
    offsetY = 0,
    offsetZ = 0;

  viewMode === 'X' ? (offsetX = 1) : viewMode === 'Y' ? (offsetY = 1) : (offsetZ = 1);

  if (!bFront) {
    offsetX *= -1;
    offsetY *= -1;
    offsetZ *= -1;
  }

  const camera = cameraControlsRef.current.camera;
  const initialPosition = new Vector3(
    centerBox.x + offsetX,
    centerBox.y + offsetY,
    centerBox.z + offsetZ
  );

  const halfSizeModel = sizeBox * 0.5;
  const halfFov = MathUtils.degToRad(camera.fov * 0.5);
  const distance = halfSizeModel / Math.tan(halfFov);
  const direction = new Vector3().subVectors(initialPosition, centerBox).normalize();
  const position = direction.multiplyScalar(distance).add(centerBox);

  camera.near = sizeBox / 100;
  camera.far = sizeBox * 100;
  camera.updateProjectionMatrix();

  if (cameraConfigRef) {
    cameraConfigRef.current = {
      far: sizeBox * 100,
      near: sizeBox / 100
    };
  }

  cameraControlsRef.current.setLookAt(
    position.x,
    position.y,
    position.z,
    centerBox.x,
    centerBox.y,
    centerBox.z,
    true
  );

  cameraControlsRef.current.fitToBox(object3D, true, {
    paddingLeft: -10,
    paddingRight: -10,
    paddingBottom: -10,
    paddingTop: -10
  });
};

/**
 * 위험성평가 데이터 이미지 구조 포맷팅
 */
export const formattedRiskAssessment = (data) => {
  const { picture, purpose } = data;

  let riskImageType = undefined;
  let riskSigleImageTitle = undefined;
  let riskSigleImageList = [];
  let riskPairImageTitle = {
    left: undefined,
    right: undefined
  };
  let riskPairImageList = {
    lefts: [],
    rights: []
  };

  if (!picture) {
    return {
      purpose,
      riskImageType,
      riskSigleImageTitle,
      riskSigleImageList,
      riskPairImageTitle,
      riskPairImageList,
      originData: data
    };
  }

  if (picture.type === 'single') {
    riskImageType = 'single';
    riskSigleImageTitle = picture.single.title;
    riskSigleImageList = picture.single.urls;
  } else if (picture.type === 'pair') {
    riskImageType = 'pair';
    riskPairImageTitle = {
      left: picture.pair.left.title,
      right: picture.pair.right.title
    };
    riskPairImageList = {
      lefts: picture.pair.left.urls,
      rights: picture.pair.right.urls
    };
  } else {
    riskImageType = undefined;
    riskSigleImageTitle = undefined;
    riskSigleImageList = [];
    riskPairImageTitle = {
      left: undefined,
      right: undefined
    };
    riskPairImageList = {
      lefts: [],
      rights: []
    };
  }

  const selectedRisk = {
    purpose,
    riskImageType,
    riskSigleImageTitle,
    riskSigleImageList,
    riskPairImageTitle,
    riskPairImageList,
    originData: data
  };

  return selectedRisk;
};

/**
 * 마커 클릭 시 카메라 위치 조정
 */
export const zoomFitMarker = (
  scene,
  modelName,
  cameraControlsRef,
  viewMode,
  bFront,
  duration = 0.6
) => {
  const object3D = scene.getObjectByName(modelName);
  if (!object3D) return;

  const box = new Box3().setFromObject(object3D);
  const sizeBox = box.getSize(new Vector3()).length();
  const centerBox = box.getCenter(new Vector3());

  const camera = cameraControlsRef.current.camera;

  const diagonalOffset = new Vector3(1, 1, 1).normalize().multiplyScalar(sizeBox);

  if (viewMode === 'X') diagonalOffset.setY(0).setZ(0);
  else if (viewMode === 'Y') diagonalOffset.setX(0).setZ(0);
  else if (viewMode === 'Z') diagonalOffset.setX(0).setY(0);

  if (!bFront) diagonalOffset.multiplyScalar(-1);

  const halfFov = MathUtils.degToRad(camera.fov * 0.5);
  const distance = (sizeBox / 2 / Math.tan(halfFov)) * 1.5;
  const finalPosition = new Vector3().addVectors(
    centerBox,
    diagonalOffset.normalize().multiplyScalar(distance)
  );

  camera.near = sizeBox / 100;
  camera.far = sizeBox * 100;
  camera.updateProjectionMatrix();

  const currentPosition = camera.position.clone();

  gsap.to(currentPosition, {
    x: finalPosition.x,
    y: finalPosition.y,
    z: finalPosition.z,
    duration: duration,
    ease: 'power2.inOut',
    onUpdate: () => {
      cameraControlsRef.current.setLookAt(
        currentPosition.x,
        currentPosition.y,
        currentPosition.z,
        centerBox.x,
        centerBox.y,
        centerBox.z,
        true
      );
    },
    onComplete: () => {
      cameraControlsRef.current.setLookAt(
        finalPosition.x,
        finalPosition.y,
        finalPosition.z,
        centerBox.x,
        centerBox.y,
        centerBox.z,
        true
      );
    }
  });
};

export const formatMobile = (text) => {
  return text?.replace(/(\d{3})(\d{4})(\d{4})/, '$1-$2-$3');
};

export const formatPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return '-';

  const cleaned = String(phoneNumber).replace(/[^\d]/g, '');

  return cleaned;
};

export const getBase64 = (img, callback) => {
  const reader = new FileReader();

  reader.addEventListener('load', () => callback(reader.result));
  reader.readAsDataURL(img);
};

/**
 * POI objectName, Floor 추출함수
 */
export const extractPoiNames = (str) => {
  const regex = /\[(.*?)\]/;
  const match = str.match(regex);

  if (match && match[1]) {
    return match[1];
  }

  return null;
};

/**
 * POI objectName, Floor 추출함수
 */
export const extractPoiFloor = (str) => {
  const regex = /\[(.*?)\]-(.*)/;
  const match = str.match(regex);

  if (match && match[2]) {
    return match[2].trim();
  }

  return null;
};

/**
 * POI scale, rotation 추출함수
 */
export const extractPoiValues = (str) => {
  const regex = /\[(.*?)\]/;
  const match = str.match(regex);

  if (match && match[1]) {
    return match[1].split(',').map((value) => parseFloat(value.trim()));
  }

  return null;
};

/**
 * 선택된 년도와 월에 따른 해당 월의 총 일수 계산
 */
export const getDaysInMonth = (year, month) => {
  return dayjs(`${year}-${month}`).daysInMonth();
};
